<script lang="ts">
  import { slide } from "svelte/transition";
  import { pop, replace } from "svelte-spa-router";

  import {
    clearSharedAccountAccess,
    ensureSharedAccountAccess,
    fetchUser,
    initiateUser,
    isClient,
    post,
    prepareSharedAccountContextFromAuthResponse,
    reconcileSharedAccountContextWithUser,
    serverlessRoutes,
    storage,
    translate,
    errorTracker,
  } from "lib";

  import { alertStore } from "stores";
  import { ButtonComponent, InputField, Svg } from "ui";

  let email = "";
  let code = "";
  let isLoading = false;
  let isCodeSent = false;

  const createRecoveryCode = async (): Promise<void> => {
    isLoading = true;

    const { error } = await post(`${serverlessRoutes.AUTH}/recovery-code`, { email });

    if (error) {
      isLoading = false;
      alertStore.show(error, "error");
      errorTracker.captureError(error as Error | string, "error", {
        context: "auth",
        action: "create_recovery_code",
        email
      });
      return;
    }

    isCodeSent = true;
    isLoading = false;
  };

  const loginWithCode = async (): Promise<void> => {
    isLoading = true;

    const { data, error } = await post(`${serverlessRoutes.AUTH}/login-with-code`, {
      email,
      code,
    });

    if (error) {
      isLoading = false;
      alertStore.show(error, "error");
      errorTracker.captureError(error as Error | string, "error", {
        context: "auth",
        action: "login_with_code",
        email
      });
      return;
    }

    const { accessToken } = data;

    await clearSharedAccountAccess();
    await storage.set("authJwt", accessToken);

    await prepareSharedAccountContextFromAuthResponse(data);

    const hasSharedAccountAccess = await ensureSharedAccountAccess();
    if (!hasSharedAccountAccess) {
      isLoading = false;
      alertStore.show("Shared account access was not granted.", "error");
      return;
    }

    const fetchedUser = await fetchUser();
    if (!fetchedUser) {
      isLoading = false;
      alertStore.show("Failed to load authenticated user.", "error");
      return;
    }

    await reconcileSharedAccountContextWithUser(fetchedUser);
    await initiateUser();

    if (!isClient(fetchedUser)) {
      replace("/clients/list");
    } else {
      replace("/training/program");
    }
  };
</script>

<div class="relative h-[100vh] w-full flex items-center justify-center">
  <Svg myClass="absolute top-8 left-8" size={24} name="left-arrow-button" on:click={pop} />

  <div class="w-max-[320px] flex flex-col gap-4">
    <div class="mb-4 text-center">
      <div class="text-xl font-bold">{translate("SIGN_IN_WITH_EMAIL_CODE")}</div>
      <div class="text-xs">{translate("RESET_PASSWORD_NOTE")}</div>
    </div>

    <form class="flex flex-col gap-4" on:submit|preventDefault={createRecoveryCode}>
      <InputField type="email" label="Email" disabled={isCodeSent} bind:value={email} />

      {#if !isCodeSent}
        <div class="flex justify-center">
          <ButtonComponent {isLoading} type="submit" disabled={!email}>
            {translate("SEND_CODE")}
          </ButtonComponent>
        </div>
      {/if}
    </form>

    {#if isCodeSent}
      <form class="flex flex-col gap-4" on:submit|preventDefault={loginWithCode} in:slide>
        <InputField type="number" label={translate("CODE")} bind:value={code} />

        <div class="flex justify-center">
          <ButtonComponent type="submit" {isLoading} disabled={!code}>
            {translate("SIGN_IN")}
          </ButtonComponent>
        </div>
      </form>
    {/if}
  </div>
</div>
